import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Select,
} from 'antd';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import StepsContainer from '../common/containers/StepsContainer';
import DivisionSelector from '../common/inputs/DivisionSelector';

import CodeCostHeaders from './costcodeHeaders';
import CostCodeUploadPreview from './costcodeUploadPreview';
import { LABOR_CATEGORY_ID } from '../helpers/costcodeHelpers';

const isEmptyCell = (value) => (
  value === undefined
  || value === null
  || (typeof value === 'string' && !value.trim())
);

const buildCustomData = (row, mapping, customFieldTemplate) => {
  if (!customFieldTemplate?.sections) {
    return {
      customData: [],
      responseMap: {},
    };
  }

  const customData = [];
  const responseMap = {};

  customFieldTemplate.sections.forEach((section) => {
    const processedFields = [];

    section.fields?.forEach((field) => {
      if (field.type !== 'text') return;

      const fieldKey = `custom_${field.id}`;
      const columnHeader = mapping[fieldKey];
      if (!columnHeader) return;

      const value = row[columnHeader];
      if (isEmptyCell(value)) return;

      const responseValue = String(value);
      responseMap[field.id] = responseValue;

      processedFields.push({
        duplicatedParentId: null,
        fieldId: field.id,
        sectionId: field.sectionId || section.id,
        title: field.title,
        type: 'text',
        response: { value: responseValue },
      });
    });

    if (processedFields.length > 0) {
      customData.push({
        name: section.name,
        duplicatedParentId: null,
        fields: processedFields,
      });
    }
  });

  return {
    customData,
    responseMap,
  };
};

function CostCodeUploadSteps({
  currentStep,
  workbook,
  onData,
  actionButtonEnabled,
  customFieldTemplate,
}) {
  const [currentSheetName, setCurrentSheetName] = useState();
  const [divisionId, setDivisionId] = useState();
  const [sheetJSONs, setSheetJSONs] = useState({});
  const [data, setData] = useState([]);
  const [mappedColumns, setMappedColumns] = useState([]);
  const [customFieldResponseMap, setCustomFieldResponseMap] = useState({});

  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const categoriesByName = useMemo(() => (
    Object.values(categoriesIdMap).reduce((acc, cat) => {
      if (cat.name) acc[cat.name.toLowerCase()] = cat;
      return acc;
    }, {})
  ), [categoriesIdMap]);

  const onHeaderSelect = useCallback((mapping = {}) => {
    setMappedColumns(Object.keys(mapping).filter((key) => mapping[key]));

    const {
      code: cc,
      description: desc,
      name,
      category,
      hourlyWage,
      dailyWage,
      wageAdjustment,
      wageMultiplier,
      hourlyBillingRate,
      dailyBillingRate,
    } = mapping;

    const rows = sheetJSONs[currentSheetName] ?? [];

    const newCustomFieldResponseMap = {};

    const newData = rows.map((row, idx) => {
      // Resolve category by name
      let resolvedCategoryIds = [LABOR_CATEGORY_ID]; // default to labor category
      if (category && row[category]) {
        const categoryName = String(row[category]).trim().toLowerCase();
        const matchedCategory = categoriesByName[categoryName];
        if (matchedCategory) resolvedCategoryIds = [matchedCategory.id];
      }

      const item = {
        code: row[cc],
        description: row[desc],
        name: row[name],
        key: idx,
        divisionId,
        categoryIds: resolvedCategoryIds,
      };

      if (category) {
        const categoryValue = row[category];
        item.category = isEmptyCell(categoryValue)
          ? 'Labour'
          : categoryValue;
      }
      if (!isEmptyCell(row[hourlyWage])) item.hourlyWage = row[hourlyWage];
      if (!isEmptyCell(row[dailyWage])) item.dailyWage = row[dailyWage];
      if (!isEmptyCell(row[wageAdjustment])) item.wageAdjustment = row[wageAdjustment];
      if (!isEmptyCell(row[wageMultiplier])) item.wageMultiplier = row[wageMultiplier];
      if (!isEmptyCell(row[hourlyBillingRate])) item.hourlyBillingRate = row[hourlyBillingRate];
      if (!isEmptyCell(row[dailyBillingRate])) item.dailyBillingRate = row[dailyBillingRate];

      const { customData, responseMap } = buildCustomData(row, mapping, customFieldTemplate);
      if (customData.length > 0) item.customData = customData;

      newCustomFieldResponseMap[item.key] = responseMap;

      return item;
    });
    onData(newData);
    setData(newData);
    setCustomFieldResponseMap(newCustomFieldResponseMap);
  }, [onData, sheetJSONs, currentSheetName, divisionId, categoriesByName, customFieldTemplate]);

  useEffect(() => {
    if (workbook) {
      const {
        Sheets,
        SheetNames = [],
      } = workbook;
      if (SheetNames.length > 0) {
        if (!currentSheetName) setCurrentSheetName(SheetNames[0]);
      }
      const newSheetMap = {};
      SheetNames.forEach((sheetName) => {
        newSheetMap[sheetName] = XLSX.utils.sheet_to_json(Sheets[sheetName], { defval: '' });
      });
      setSheetJSONs(newSheetMap);
    } else {
      setCurrentSheetName();
      setSheetJSONs({});
    }
  }, [workbook, currentSheetName]);

  const {
    defaultSheetName,
    sheetOpts = [],
  } = useMemo(() => {
    if (!workbook) return {};
    const { SheetNames = [] } = workbook;
    return {
      defaultSheetName: SheetNames[0],
      sheetOpts: SheetNames.map((sheet) => ({ value: sheet, label: sheet })),
    };
  }, [workbook]);

  const ourFirstRow = useMemo(() => {
    const {
      [currentSheetName]: [firstRow = {}] = [],
    } = sheetJSONs;
    return firstRow;
  }, [sheetJSONs, currentSheetName]);
  const headerKey = Object.keys(ourFirstRow).join('|');

  useEffect(() => {
    actionButtonEnabled(divisionId);
  },[divisionId]);

  return (
    <StepsContainer
      currentStep={currentStep}
      steps={[{
        title: 'Select Sheet',
        content: (
          <Row gutter={20}>
            <Col>
              <Select
                style={{ width: 250 }}
                defaultValue={defaultSheetName}
                onSelect={setCurrentSheetName}
                options={sheetOpts}
              />
            </Col>
            <Col>
              <DivisionSelector
                style={{ width: 250 }}
                divisionId={divisionId}
                onChange={setDivisionId}
              />
            </Col>
          </Row>
        ),
        key: 'select'
      },{
        title: 'Select Headers',
        content: (
          <CodeCostHeaders
            key={headerKey}
            headers={ourFirstRow}
            onSelect={onHeaderSelect}
            customFieldTemplate={customFieldTemplate}
          />
        ),
        key: 'headers'
      }, {
        title: 'Preview',
        content: (
          <CostCodeUploadPreview
            dataSource={data}
            actionButtonEnabled={actionButtonEnabled}
            mappedColumns={mappedColumns}
            customFieldTemplate={customFieldTemplate}
            customFieldResponseMap={customFieldResponseMap}
            shouldValidate={currentStep === 1}
            scroll={{ x: 'max-content' }}
          />
        ),
        key: 'preview'
      }]}
    />
  );
};

CostCodeUploadSteps.propTypes = {
  currentStep: PropTypes.number.isRequired,
  workbook: PropTypes.shape({
    Sheets: PropTypes.shape({}),
    SheetNames: PropTypes.arrayOf(PropTypes.string),
  }),
  onData: PropTypes.func.isRequired,
  actionButtonEnabled: PropTypes.func.isRequired,
  customFieldTemplate: PropTypes.shape({
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        sectionId: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
      })),
    })),
  }),
};

CostCodeUploadSteps.defaultProps = {
  workbook: null,
  customFieldTemplate: null,
};

export default CostCodeUploadSteps;
